import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { ACCESS_TOKEN_KEY, AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { ShareMode } from 'src/app/core/authentication/share-token';
import { User } from 'src/app/shared/user/user';

import { License } from '../../user/license';
import { ProjectStreamService } from '../current-project/project-stream.service';
import { CloneUtils } from '../utility/clone-utils';
import { CurrentUserService } from './current-user.service';

// Included in script tag inside index.html
declare const aptrinsic: any;

@Injectable({
    providedIn: 'root'
})
export class CurrentUserStreamService {
    stream = new BehaviorSubject<User>(null);

    public currentUserWithRoleStream = new BehaviorSubject<User>(null);

    constructor(
        private currentUserService: CurrentUserService,
        private projectStream: ProjectStreamService,
        private authService: AuthenticationService
    ) {}

    set currentUser(user: User) {
        this.stream.next(user);
        this.loadCurrentUserWithRole();
    }

    get currentUser(): User {
        return this.stream.getValue();
    }

    async initCurrentUser(): Promise<void> {
        const user = await this.currentUserService.getCurrentUser();
        this.currentUser = user;
    }

    async initRegisteredUser(shareMode: ShareMode): Promise<User> {
        const currentUser = this.stream.getValue();
        if (currentUser) {
            return currentUser;
        }
        if (!sessionStorage.getItem(ACCESS_TOKEN_KEY) && shareMode === ShareMode.SIGNED_IN_USER) {
            // Connect does not provide access token in this mode - neither in the URL nor in shares/token response
            window.location.href = await this.authService.getLoginUrl();
        } else {
            const user = await this.currentUserService.getCurrentUser();
            this.currentUser = user;
            return user;
        }
    }

    async getUserLicenseDetail(): Promise<License> {
        if (this.currentUser.license) {
            return Promise.resolve(this.currentUser.license);
        } else {
            return new Promise((resolve, reject) => {
                this.currentUserService
                    .getLicenseDetail()
                    .then(license => {
                        let user = CloneUtils.cloneDeep(this.currentUser);
                        user.license = license;
                        this.currentUser = user;
                        resolve(license);
                    })
                    .catch(() => {
                        reject(null);
                    });
            });
        }
    }

    loadCurrentUserWithRole(): void {
        this.projectStream.currentProjectStream.pipe(filter(Boolean), take(1)).subscribe(() => {
            this.projectStream
                .getUserWithRolesForCurrentProject(this.currentUser.id, this.currentUser.tiduuid)
                .then(userWithRole => {
                    // Send user info to Gainsight
                    aptrinsic(
                        'identify',
                        {
                            //User Fields
                            id: userWithRole.tiduuid
                        },
                        {
                            //Account Fields
                            id: this.projectStream.currentProject.id
                        }
                    );
                    this.currentUserWithRoleStream.next(userWithRole);
                });
        });
    }
}
